import DayPickerRenderer from './DayPickerRenderer';
import { observer } from 'mobx-react-lite';
import {
    CalendarModuleHamburgerButton,
    CalendarModuleNewEventButton,
} from 'owa-calendar-commandbar';
import { getSearchState } from 'owa-calendar-search-view-store';
import loc, { format } from 'owa-localize';
import { jumpToRegion } from 'owa-locstrings/lib/strings/jumptoregion.locstring.json';
import { exitSearch } from 'owa-search-actions';
import { SearchScenarioId, getScenarioStore } from 'owa-search-store';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import CalendarLeftPanelToggleButton from './CalendarLeftPanelToggleButton';
import * as React from 'react';
import {
    CalendarLayoutLeftPaneConstants,
    calculateCalendarsListPaneWidth,
    getLeftPaneMinWidth,
    shouldShowLeftNavAsOverlay,
    getIsLeftNavShown,
} from 'owa-calendar-layout';
import { dateSelectionRegionName, calendarListRegionName } from './CalendarModule.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import CalendarPickerLeftNav from './CalendarPickerLeftNav';
import styles from './CalendarLeftPane.scss';
interface CalendarLeftPaneProps {
    leftNavWidthOverride?: number;
    toggleHamburgerButton: () => void;
    shouldAutoFocusHamburgerButton: boolean;
}

const SCROLL_WHOLE_LEFTPANE_BREAKPOINT = 256;

export default observer(function CalendarLeftPane(props: CalendarLeftPaneProps) {
    const leftPaneMinWidth = getLeftPaneMinWidth();
    const calendarSidePanelIsExpanded = getIsLeftNavShown();
    const isCollapsibleLeftNav = isFeatureEnabled('mon-tri-collapsibleFolderPane');

    const [isScroll, setIsScroll] = React.useState<boolean>(false);

    React.useEffect(() => {
        const container = document.getElementById('leftPaneContainer');
        if (container) {
            handleResize();
        }

        function handleResize() {
            const scroll = container
                ? container.clientHeight < SCROLL_WHOLE_LEFTPANE_BREAKPOINT
                : false;
            setIsScroll(scroll);
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className={styles.mainContent} id={'leftPaneContainer'}>
            {isCollapsibleLeftNav && !calendarSidePanelIsExpanded && (
                <div className={styles.leftPaneCollapsed}>
                    <CalendarLeftPanelToggleButton
                        onToggle={props.toggleHamburgerButton}
                        autoFocus={props.shouldAutoFocusHamburgerButton}
                    />
                </div>
            )}
            {calendarSidePanelIsExpanded && (
                <div
                    className={isScroll ? styles.leftPaneScroll : styles.leftPane}
                    style={{
                        width: props.leftNavWidthOverride ?? calculateCalendarsListPaneWidth(),
                        minWidth: leftPaneMinWidth,
                    }}
                    data-min-width={leftPaneMinWidth}
                    data-max-width={CalendarLayoutLeftPaneConstants.leftPaneMaxWidth}
                    onClick={onLeftPaneClick}>
                    {!shouldShowLeftNavAsOverlay() && !isFeatureEnabled('mon-ribbon') && (
                        <div className={styles.leftPaneFirstRow}>
                            <CalendarModuleHamburgerButton
                                autoFocus={props.shouldAutoFocusHamburgerButton}
                                isPaneExpanded={calendarSidePanelIsExpanded}
                                toggleHamburgerButton={props.toggleHamburgerButton}
                            />
                            <div className={styles.leftPaneCommandBarWrapper}>
                                <CalendarModuleNewEventButton />
                            </div>
                        </div>
                    )}
                    {isCollapsibleLeftNav && !shouldShowLeftNavAsOverlay() && (
                        <div className={styles.leftPaneHamburgerRow}>
                            <CalendarLeftPanelToggleButton
                                onToggle={props.toggleHamburgerButton}
                                autoFocus={props.shouldAutoFocusHamburgerButton}
                            />
                        </div>
                    )}
                    {isScroll ? (
                        <SkipLinkRegion
                            skipLinkName={format(loc(jumpToRegion), loc(dateSelectionRegionName))}
                            role="navigation"
                            className={styles.dayPicker}
                            regionName={loc(dateSelectionRegionName)}>
                            <DayPickerRenderer />
                            <CalendarPickerLeftNav />
                        </SkipLinkRegion>
                    ) : (
                        <>
                            <SkipLinkRegion
                                skipLinkName={format(
                                    loc(jumpToRegion),
                                    loc(dateSelectionRegionName)
                                )}
                                role="navigation"
                                className={styles.dayPicker}
                                regionName={loc(dateSelectionRegionName)}>
                                <DayPickerRenderer />
                            </SkipLinkRegion>
                            <SkipLinkRegion
                                skipLinkName={format(
                                    loc(jumpToRegion),
                                    loc(calendarListRegionName)
                                )}
                                role="complementary"
                                regionName={loc(calendarListRegionName)}
                                className={styles.calendarPickerContainer}>
                                <CalendarPickerLeftNav />
                            </SkipLinkRegion>
                        </>
                    )}
                </div>
            )}
        </div>
    );
});

function onLeftPaneClick() {
    if (getSearchState() || getScenarioStore(SearchScenarioId.Calendar).searchSessionGuid) {
        exitSearch('LeftNavClick', SearchScenarioId.Calendar);
    }
}
